// VideoStoryComponents.js - 提取的小组件

import React, { useState, useEffect, useCallback, useRef } from "react";
import { Clock, ChevronUp, ChevronDown, Eye, Check, AlertCircle, BookOpen } from "lucide-react";
import EventNavigation from './EventNavigation';
import EventNoteSystem from './EventNoteSystem';
import { getConfidenceColor } from "../TIMEBAR/EventGroupHelper"; // 添加缺失的导入
import { useSharedEvents } from "../TIMEBAR/SharedEventsContext";

// 提取到单独的Modal组件，避免状态变化引起主组件重新渲染
const EventActionModal = React.memo(({ 
  showModal, 
  setShowModal, 
  event, 
  timeframe, 
  handleSaveToWorkspace,
  handleShareToShared,
  eventLabels,
  createStoryTimeStamp,
  getPriorityLabel
}) => {
  const [modalNote, setModalNote] = useState('');
  const [modalShareMsg, setModalShareMsg] = useState('');
  const [actionRequired, setActionRequired] = useState("monitor");
  
  // 当Modal打开或关闭时重置表单状态
  useEffect(() => {
    if (!showModal) {
      setModalNote('');
      setModalShareMsg('');
      setActionRequired("monitor");
    }
  }, [showModal]);
  
  if (!showModal) return null;
  
  return (
    <div className="storyboard-modal-overlay" onClick={e => e.stopPropagation()}>
      <div className="storyboard-modal">
        <div className="modal-header">
          <h3>{showModal.type === 'save' ? 'Add to Workspace' : 'Share with Team'}</h3>
          <button className="modal-close" onClick={() => setShowModal(null)}>
            <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-x"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
          </button>
        </div>
        <div className="modal-content">
          {/* Event preview */}
          <div className="modal-event-preview">
            <div className="preview-image">
              <img 
                src={event.imagePath || `/api/placeholder/200/200?text=Robot ${event.robotId}`}
                alt={event.event}
              />
            </div>
            <div className="preview-info">
              <h4>{event.event}</h4>
              <div className="preview-details">
                <span className="preview-type">{eventLabels[event.type || 1]}</span>
                <span className="preview-time">{createStoryTimeStamp(timeframe, event.startTime)}</span>
                <span className="preview-unit">Unit {event.robotId}</span>
                <span className="preview-priority">{getPriorityLabel(event.priority || 3)}</span>
              </div>
            </div>
          </div>
          
          {/* Note/Share form */}
          {showModal.type === 'save' && (
            <div className="modal-form">
              <div className="form-group">
                <label>Add Notes</label>
                <textarea
                  value={modalNote}
                  onChange={e => setModalNote(e.target.value)}
                  placeholder="Add details about this evidence..."
                  rows={4}
                  onClick={e => e.stopPropagation()}
                />
              </div>
            </div>
          )}
          
          {showModal.type === 'share' && (
            <div className="modal-form">
              <div className="form-group">
                <label>Message for Team</label>
                <textarea
                  value={modalShareMsg}
                  onChange={e => setModalShareMsg(e.target.value)}
                  placeholder="Add context or instructions for your team..."
                  rows={4}
                  onClick={e => e.stopPropagation()}
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Action Required</label>
                  <select 
                    value={actionRequired}
                    onChange={(e) => setActionRequired(e.target.value)}
                    onClick={e => e.stopPropagation()}
                  >
                    <option value="dispatch">Immediate Dispatch</option>
                    <option value="investigate">Investigate ASAP</option>
                    <option value="monitor">Monitor & Update</option>
                    <option value="reference">For Reference Only</option>
                  </select>
                </div>
              </div>
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button className="modal-btn cancel" onClick={() => setShowModal(null)}>
            Cancel
          </button>
          {showModal.type === 'save' && (
            <button 
              className="modal-btn save" 
              onClick={() => {
                handleSaveToWorkspace(event, modalNote);
                setShowModal(null);
              }}
              disabled={!modalNote.trim()}
            >
              <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-bookmark"><path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"></path></svg>
              Save to Workspace
            </button>
          )}
          {showModal.type === 'share' && (
            <button 
              className="modal-btn share" 
              onClick={() => {
                handleShareToShared(event, modalShareMsg, actionRequired);
                setShowModal(null);
              }}
              disabled={!modalShareMsg.trim()}
            >
              <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-share-2"><circle cx="18" cy="5" r="3"></circle><circle cx="6" cy="12" r="3"></circle><circle cx="18" cy="19" r="3"></circle><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"></line><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"></line></svg>
              Share with Team
            </button>
          )}
        </div>
      </div>
    </div>
  );
});

// ScrollPreview 组件 - 显示事件预览
export const ScrollPreview = ({ event, isVisible, position, timeframe, getEventIcon, getPriorityColor, getPriorityLabel, createStoryTimeStamp }) => {
  if (!event || !isVisible) return null;
  
  // 计算位置样式
  const positionStyle = {
    top: position === 'top' ? '20%' : position === 'bottom' ? '80%' : '50%',
  };
  
  return (
    <div 
      className="scroll-preview performance-optimized" 
      style={positionStyle}
    >
      <div className="scroll-preview-header">
        <div className="scroll-preview-icon">
          {getEventIcon(event.type || 1)}
        </div>
        <div className="scroll-preview-title">
          {event.event}
        </div>
      </div>
      
      <div className="scroll-preview-content">
        <div className="scroll-preview-event-title">
          {event.event}
        </div>
        <div className="scroll-preview-time">
          {createStoryTimeStamp(timeframe, event.startTime)}
        </div>
        <div 
          className="scroll-preview-priority"
          style={{ backgroundColor: getPriorityColor(event.priority || 3) }}
        >
          {getPriorityLabel(event.priority || 3)}
        </div>
      </div>
      
      <div className="scroll-preview-arrow">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 5v14M19 12l-7 7-7-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </div>
    </div>
  );
};

// JumpMarker 组件 - 显示跳转标记
export const JumpMarker = ({ isVisible, position }) => {
  if (!isVisible) return null;
  
  return (
    <div 
      className="jump-marker performance-optimized" 
      style={{ 
        top: position.y - 20,
        left: position.x - 20
      }}
    />
  );
};

// KeyboardHint 组件 - 显示键盘快捷键提示
export const KeyboardHint = ({ isVisible }) => {
  if (!isVisible) return null;
  
  return (
    <div className={`keyboard-hint ${isVisible ? 'visible' : ''}`}>
      <span>Keyboard Navigation:</span>
      <span className="key">↑</span>
      <span className="key">↓</span>
      <span>to navigate events</span>
    </div>
  );
};

// StoryHeader 组件 - 故事标题和头部
export const StoryHeader = ({ 
  storyTitle, 
  storyProgress, 
  isHeaderExpanded, 
  toggleHeader, 
  events, 
  groupedByPriority,
  priorityOrder,
  getPriorityColor,
  getPriorityLabel,
  handleEventActivation,
  activeEventId,
  sortMethod,
  setSortMethod,
  timeframe
}) => {
  return (
    <div className={`story-header fixed compact-layout`}>
      {/* 第一行：标题和进度条 */}
      <div className="header-top-row">
        <div className="story-title-wrapper">
          <h1 className="story-title">
            <BookOpen size={18} className="story-icon" />
            {storyTitle}
          </h1>
          <div className="header-controls">
            <button 
              className="header-toggle-btn"
              onClick={toggleHeader}
              aria-label={isHeaderExpanded ? "Collapse header" : "Expand header"}
            >
              {isHeaderExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </button>
          </div>
        </div>
        <div className="story-progress">
          <div 
            className="story-progress-bar performance-optimized" 
            style={{ width: `${storyProgress}%` }} 
          />
        </div>
      </div>
      
      {/* 第二行：事件总结，只在展开时显示 */}
      {isHeaderExpanded && (
        <div className="story-summary">
          MRVS identified {events.length} events that may require your attention.
        </div>
      )}
      
      {/* 导航组件 */}
      <div className={`navigation-container ${isHeaderExpanded ? 'expanded' : 'compact'}`}>
        <EventNavigation 
          groupedByPriority={groupedByPriority}
          priorityOrder={priorityOrder}
          getPriorityColor={getPriorityColor}
          getPriorityLabel={getPriorityLabel}
          handleEventActivation={handleEventActivation}
          activeEventId={activeEventId}
        />
      </div>

      {/* 优先级概述和排序切换器 */}
      <div className="priority-overview">
        <div className="sort-toggle-container">
          <span className="sort-label">Sort by:</span>
          <div className="sort-toggle">
            <button 
              className={`sort-option ${sortMethod === 'time' ? 'active' : ''}`}
              onClick={() => setSortMethod('time')}
            >
              <Clock size={12} />
              <span>time</span>
            </button>
            <button 
              className={`sort-option ${sortMethod === 'priority' ? 'active' : ''}`}
              onClick={() => setSortMethod('priority')}
            >
              <AlertCircle size={12} />
              <span>urgency</span>
            </button>
          </div>
        </div>
      </div>

      <div className="story-meta">
        <div className="story-time">
          <Clock size={14} className="story-time-icon" />
          <span className="story-time-text">{timeframe}</span>
        </div>
      </div>
    </div>
  );
};

// ScrollIndicator 组件 - 滚动指示器
export const ScrollIndicator = () => {
  return (
    <div className="scroll-indicator-container">
      <div className="scroll-down-indicator">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 5L12 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M19 12L12 19L5 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
        <span>Scroll to Explore</span>
      </div>
    </div>
  );
};

// ObjectTrackingSection 组件 - 对象跟踪部分 (更新版)
export const ObjectTrackingSection = React.memo(({ event, index, trackedObjects, toggleTracking }) => {
  if (!event || !event.bboxes || !Array.isArray(event.bboxes) || event.bboxes.length === 0) {
    return null;
  }
  
  return (
    <div className="story-objects-inline">
      {event.bboxes.map((bbox, boxIndex) => {
        const isTracking = trackedObjects[index] && trackedObjects[index][`box${boxIndex}`];
        const category = bbox.category || 'object';
        
        return (
          <button 
            key={boxIndex}
            className={`story-track-btn-inline ${isTracking ? 'tracking' : ''}`}
            onClick={() => toggleTracking(index, boxIndex)}
          >
            {category} {boxIndex + 1}
            {isTracking ? (
              <span className="tracking-icon">✓</span>
            ) : (
              <span className="tracking-icon">+</span>
            )}
          </button>
        );
      })}
    </div>
  );
});

// EventItem 组件 - 单个事件项 (修改后版本，优化渲染性能)
export const EventItem = React.memo(({
  event,
  index,
  isSelected,
  isActive,
  animatingEventId,
  getPriorityColor,
  eventLabels,
  getEventIcon,
  getPriorityLabel,
  createStoryTimeStamp, 
  timeframe,
  formatTime,
  hasTrackedObjects,
  handleEventClick,
  getStoryDescription,
  robotId,
  hasBboxes,
  handleAddToOtherArea,
  toggleTracking,
  trackedObjects,
  isGrouped
}) => {
  const eventType = event.type || 1;
  const confidence = event.confidence || "medium";
  const isTracking = hasTrackedObjects(index);
  const timeStamp = createStoryTimeStamp(timeframe, event.startTime);
  const storyDesc = getStoryDescription(event, robotId);
  const [isEvidenceExpanded, setIsEvidenceExpanded] = useState(false);
  const [eventNotes, setEventNotes] = useState([]);
  const [showModal, setShowModal] = useState(null);
  const { addSharedEvent, addToWorkspace } = useSharedEvents();
  
  // 使用 useCallback 优化事件处理函数
  const handleEvidenceToggle = useCallback((e) => {
    e.stopPropagation(); // 阻止事件冒泡
    setIsEvidenceExpanded(prev => !prev);
  }, []);
  
  const handleNotesChange = useCallback((notes) => {
    setEventNotes(notes);
  }, []);

  const handleSaveToWorkspace = useCallback((event, note) => {
    if (!event) return;
    const eventId = event.id || `${event.robotId}-${event.startTime}-${Date.now()}`;
    const noteObj = {
      id: Date.now().toString(),
      content: note,
      timestamp: new Date().toISOString(),
      isImportant: false,
      category: 'uncategorized',
    };
    
    // 获取现有 savedTo 值或创建新数组
    const existingSavedTo = Array.isArray(event.savedTo) ? [...event.savedTo] : (event.savedTo ? [event.savedTo] : []);
    // 添加 'workspace'（如果尚未包含）
    if (!existingSavedTo.includes('workspace')) {
      existingSavedTo.push('workspace');
    }
    
    const eventToSave = {
      ...event,
      id: eventId,
      notes: [noteObj],
      timestamp: new Date().toISOString(),
      savedTo: existingSavedTo,
      evidenceCategory: 'uncategorized',
      evidenceImportance: 'medium',
      viewed: false,
    };
    if (addToWorkspace) {
      addToWorkspace(eventToSave);
    } else {
      addSharedEvent(eventToSave);
    }
  }, [addToWorkspace, addSharedEvent]);

  const handleShareToShared = useCallback((event, msg, priority) => {
    if (!event) return;
    const eventId = event.id || `${event.robotId}-${event.startTime}-${Date.now()}`;
    const messageObj = {
      id: Date.now().toString(),
      sender: "Current Officer",
      message: msg,
      timestamp: new Date().toISOString(),
    };
    
    // 获取现有 savedTo 值或创建新数组
    const existingSavedTo = Array.isArray(event.savedTo) ? [...event.savedTo] : (event.savedTo ? [event.savedTo] : []);
    // 添加 'shared'（如果尚未包含）
    if (!existingSavedTo.includes('shared')) {
      existingSavedTo.push('shared');
    }
    
    const eventToShare = {
      ...event,
      robotId: event.robotId || robotId,
      id: eventId,
      messages: [messageObj],
      timestamp: new Date().toISOString(),
      savedTo: existingSavedTo,
      shareMessage: msg,
      priority: priority === "high" ? 1 : priority === "medium" ? 2 : 3,
      viewed: false,
      actionRequired: priority,
    };
    addSharedEvent(eventToShare);
  }, [addSharedEvent, robotId]);
  
  // 处理章节点击
  const handleChapterClick = useCallback((e) => {
    e.stopPropagation();
    handleEventClick(index);
  }, [handleEventClick, index]);
  
  return (
    <div
      id={`story-chapter-${index}`}
      className={`story-chapter performance-optimized ${
        isActive ? "story-chapter-active" : isSelected ? "story-chapter-selected" : ""
      } ${animatingEventId === index ? "story-highlight" : ""}`}
      style={{ 
        animationDelay: `${index * 0.1}s`,
        opacity: 0,
        borderLeft: `4px solid ${getPriorityColor(event.priority || 3)}`
      }}
      data-original-index={event.originalIndex}
      data-event-id={event.originalIndex}
      onClick={handleChapterClick}
    >
      {/* Progress indicator */}
      <div 
        className="story-progress-indicator"
        style={{ 
          height: isActive ? "100%" : "0%",
          transition: "height 0.6s ease-out"
        }}
      />
      
      {/* Chapter title */}
      <div 
        className="story-chapter-title"
        onClick={() => handleEventClick(index)}
      >
        <div className="story-chapter-icon">
          {getEventIcon(eventType)}
        </div>
        
        <div className="story-chapter-header">
          <div className="story-chapter-label">
            <span className="story-label">
              {eventLabels[eventType] || "Event"}
            </span>
            <span className="story-chapter-number">
              {index + 1}
            </span>
            {isTracking && (
              <span className="story-tracking-badge">Tracking</span>
            )}
            {/* Priority badge */}
            <span 
              className="story-priority-badge"
              style={{ 
                backgroundColor: getPriorityColor(event.priority || 3),
                color: 'white',
                fontSize: '0.6875rem',
                borderRadius: '9999px',
                padding: '0.125rem 0.375rem',
                marginLeft: '0.5rem'
              }}
            >
              {getPriorityLabel(event.priority || 3)}
            </span>
          </div>

          <h3 className="story-chapter-name">
            {event.event}
          </h3>
          
          <div className="story-chapter-time">
            <span className="story-timestamp">{timeStamp}</span>
            <span className="story-exact-time">
              [{formatTime(event.startTime)}]
            </span>
          </div>

          {/* Save/Share Action Buttons - now in chapter head, larger */}
          <div className="chapter-actions-large">
            <button 
              className="police-action-btn save large"
              onClick={e => {
                e.stopPropagation();
                setShowModal({ type: 'save', event });
              }}
              title="Add to workspace with notes"
            >
              <svg width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-bookmark"><path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"></path></svg>
              Save
            </button>
            <button 
              className="police-action-btn share large"
              onClick={e => {
                e.stopPropagation();
                setShowModal({ type: 'share', event });
              }}
              title="Share with team"
            >
              <svg width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-share-2"><circle cx="18" cy="5" r="3"></circle><circle cx="6" cy="12" r="3"></circle><circle cx="18" cy="19" r="3"></circle><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"></line><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"></line></svg>
              Share
            </button>
          </div>
        </div>
      </div>

      {/* Chapter content - 始终显示，不使用expanded类 */}
      <div className="story-chapter-content expanded">
        {/* Story image */}
        <div className="story-image-container">
          <img
            src={event.imagePath || `/api/placeholder/300/200?text=Event Type ${eventType}`}
            className="story-image"
            alt={`Event ${index + 1}`}
            onError={(e) => {
              e.target.src = `/api/placeholder/300/200?text=Event Type ${eventType}`;
              e.target.alt = "Image unavailable";
            }}
          />
        </div> 
        
        {/* Confidence indicator - 只在非分组事件时显示 */}
        {!isGrouped && (
          <div className="story-confidence">
            <span className="story-confidence-label">AI Confidence:</span>
            <div 
              className={`story-confidence-indicator story-confidence-${confidence}`}
            >
              <div className="story-confidence-dots">
                <span className={`dot ${confidence !== 'low' ? 'active' : ''}`}></span>
                <span className={`dot ${confidence === 'high' ? 'active' : ''}`}></span>
                <span className={`dot ${confidence === 'high' ? 'active' : ''}`}></span>
              </div>
              <span className="story-confidence-text">
                {confidence === 'high' ? 'High' : confidence === 'medium' ? 'Medium' : 'Low'}
              </span>
            </div>
          </div>
        )}

        <ObjectTrackingSection 
          event={event} 
          index={index} 
          trackedObjects={trackedObjects} 
          toggleTracking={toggleTracking} 
        />
        
        {/* Story description - 只在非分组事件时显示 */}
        {!isGrouped && (
          <div className="story-description">
            <p>{storyDesc.replace(/["']/g, '')}</p>
          </div>
        )}
        
        {/* Grouped event content */}
        {isGrouped && event.groupedEvents && (
          <SubEvents event={event} getConfidenceColor={getConfidenceColor} />
        )}
        
        {/* Evidence section - 只在非分组事件且有justification时显示 */}
        {!isGrouped && event.justification && (
          <div className="story-evidence">
            <button 
              className={`story-evidence-toggle ${isEvidenceExpanded ? 'expanded' : ''}`}
              onClick={handleEvidenceToggle}
            >
              <span className="story-evidence-title">Evidence</span>
              <span className="story-evidence-arrow">
                {isEvidenceExpanded ? '▼' : '▶'}
              </span>
            </button>
            {isEvidenceExpanded && (
              <div className="story-evidence-content">
                <ol className="story-evidence-list">
                  {event.justification.split(/[.!?]+/)
                    .filter(point => {
                      const trimmed = point.trim();
                      return trimmed.length > 0 && 
                             !/^\d+\.?$/.test(trimmed) && 
                             !/^["']$/.test(trimmed) &&
                             !/^[.,;:!?]+$/.test(trimmed);
                    })
                    .map((point, idx) => (
                      <li key={idx} className="story-evidence-item">
                        {point.trim().replace(/,\s*$/, '').replace(/["']/g, '')}
                      </li>
                    ))}
                </ol>
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* 添加备注系统 */}
      <EventNoteSystem 
        eventId={index}
        initialNotes={eventNotes}
        onNotesChange={handleNotesChange}
        onAddToOtherArea={(eventData) => handleAddToOtherArea(eventData)}
        hideSaveButton={true}
        hideNoteToggleBtn={true}
      />

      {/* 使用提取的Modal组件，而非内联定义 */}
      {showModal && (
        <EventActionModal
          showModal={showModal}
          setShowModal={setShowModal}
          event={event}
          timeframe={timeframe}
          handleSaveToWorkspace={handleSaveToWorkspace}
          handleShareToShared={handleShareToShared}
          eventLabels={eventLabels}
          createStoryTimeStamp={createStoryTimeStamp}
          getPriorityLabel={getPriorityLabel}
        />
      )}
    </div>
  );
}, (prevProps, nextProps) => {
  // 自定义比较函数，只比较必要的属性，提高性能
  return (
    prevProps.event === nextProps.event &&
    prevProps.isSelected === nextProps.isSelected &&
    prevProps.isActive === nextProps.isActive &&
    prevProps.animatingEventId === nextProps.animatingEventId &&
    prevProps.trackedObjects === nextProps.trackedObjects
  );
});

// SubEventEvidence 组件
const SubEventEvidence = ({ justification }) => {
  const [isEvidenceExpanded, setIsEvidenceExpanded] = useState(false);
  
  return (
    <div className="story-evidence">
      <button 
        className={`story-evidence-toggle ${isEvidenceExpanded ? 'expanded' : ''}`}
        onClick={() => setIsEvidenceExpanded(!isEvidenceExpanded)}
      >
        <span className="story-evidence-title">Evidence</span>
        <span className="story-evidence-arrow">
          {isEvidenceExpanded ? '▼' : '▶'}
        </span>
      </button>
      {isEvidenceExpanded && (
        <div className="story-evidence-content">
          <ol className="story-evidence-list">
            {justification.split(/[.!?]+/)
              .filter(point => {
                const trimmed = point.trim();
                return trimmed.length > 0 && 
                       !/^\d+\.?$/.test(trimmed) && 
                       !/^["']$/.test(trimmed) &&
                       !/^[.,;:!?]+$/.test(trimmed);
              })
              .map((point, idx) => (
                <li key={idx} className="story-evidence-item">
                  {point.trim().replace(/,\s*$/, '').replace(/["']/g, '')}
                </li>
              ))}
          </ol>
        </div>
      )}
    </div>
  );
};

// SubEvents 组件 - 子事件列表
export const SubEvents = ({ event, getConfidenceColor }) => {
  return (
    <div className="story-subevents">
      <h4 className="story-subevents-title">Detected Events:</h4>
      <div className="story-subevents-list">
        {event.groupedEvents.map((subEvent, subIdx) => {
          const subEventConfidence = subEvent.confidence || "medium";
          
          return (
            <div 
              key={subIdx}
              className="story-subevent"
            >
              <div className="story-subevent-header">
                <span 
                  className="story-subevent-name"
                  style={{ color: getConfidenceColor(subEventConfidence) }}
                >
                  {subEvent.event}
                </span>
                <div className="story-confidence">
                  <span className="story-confidence-label">AI Confidence:</span>
                  <div 
                    className={`story-confidence-indicator story-confidence-${subEventConfidence}`}
                  >
                    <div className="story-confidence-dots">
                      <span className={`dot ${subEventConfidence !== 'low' ? 'active' : ''}`}></span>
                      <span className={`dot ${subEventConfidence === 'high' ? 'active' : ''}`}></span>
                      <span className={`dot ${subEventConfidence === 'high' ? 'active' : ''}`}></span>
                    </div>
                    <span className="story-confidence-text">
                      {subEventConfidence === 'high' ? 'High' : subEventConfidence === 'medium' ? 'Medium' : 'Low'}
                    </span>
                  </div>
                </div>
              </div>
              <p className="story-subevent-summary">{subEvent.summary.replace(/["']/g, '')}</p>
              
              {/* Evidence section for sub-event */}
              {subEvent.justification && (
                <SubEventEvidence justification={subEvent.justification} />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// EmptyState 组件 - 空状态
export const EmptyState = () => {
  return (
    <div className="story-empty">
      <p className="story-empty-text">No robot events found</p>
      <p className="story-empty-subtext">Please select a different timeframe or robot</p>
    </div>
  );
};

// Re-export components from original files for convenience
export { default as EventNavigation } from "./EventNavigation";
export { default as EventNoteSystem } from "./EventNoteSystem";